import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';

interface MigrationManagerProps {
  onMigrationComplete?: () => void;
}

interface MigrationStatus {
  totalPlans: number;
  plansWithPricing: number;
  plansWithAllCurrencies: number;
  plansNeedingMigration: number;
}

const MigrationManager: React.FC<MigrationManagerProps> = ({ onMigrationComplete }) => {
  const [isMigrating, setIsMigrating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | null>(null);
  const [lastMigrationResult, setLastMigrationResult] = useState<string | null>(null);

  const validateMigration = async () => {
    try {
      setIsValidating(true);
      const response = await callAPI('admin/migration/validate-migration', null, 'GET');

      if (response.status === HTTPStatusEnum.OK) {
        setMigrationStatus(response.data ? JSON.parse(response.data) : null);
        Toast('Migration validation completed successfully');
      } else {
        Toast(response.message || 'Validation failed', true);
      }
    } catch (error) {
      console.error('Validation error:', error);
      Toast('Failed to validate migration', true);
    } finally {
      setIsValidating(false);
    }
  };

  const runMigration = async () => {
    try {
      setIsMigrating(true);
      const response = await callAPI('admin/migration/migrate-plans', {}, 'POST');

      if (response.status === HTTPStatusEnum.OK) {
        setLastMigrationResult(response.message);
        Toast('Migration completed successfully');
        if (onMigrationComplete) {
          onMigrationComplete();
        }
        // Refresh validation status
        await validateMigration();
      } else {
        Toast(response.message || 'Migration failed', true);
      }
    } catch (error) {
      console.error('Migration error:', error);
      Toast('Failed to run migration', true);
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Multi-Currency Migration</h2>
        <div className="flex gap-2">
          <button
            onClick={validateMigration}
            disabled={isValidating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faSync} spin={isValidating} />
            {isValidating ? 'Validating...' : 'Validate Migration'}
          </button>

          <button
            onClick={runMigration}
            disabled={isMigrating || isValidating}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faSync} spin={isMigrating} />
            {isMigrating ? 'Migrating...' : 'Run Migration'}
          </button>
        </div>
      </div>

      {lastMigrationResult && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
            <span className="text-green-800 font-medium">Last Migration Result:</span>
          </div>
          <p className="text-green-700 mt-1">{lastMigrationResult}</p>
        </div>
      )}

      {migrationStatus && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700">Migration Status</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{migrationStatus.totalPlans}</div>
              <div className="text-sm text-blue-800">Total Plans</div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{migrationStatus.plansWithPricing}</div>
              <div className="text-sm text-green-800">Plans with Multi-Currency</div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{migrationStatus.plansWithAllCurrencies}</div>
              <div className="text-sm text-purple-800">Complete Multi-Currency</div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{migrationStatus.plansNeedingMigration}</div>
              <div className="text-sm text-yellow-800">Need Migration</div>
            </div>
          </div>

          {migrationStatus.plansNeedingMigration > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />
                <span className="text-yellow-800 font-medium">Migration Required</span>
              </div>
              <p className="text-yellow-700 mt-1">
                {migrationStatus.plansNeedingMigration} plan(s) still need to be migrated to support multi-currency
                pricing.
              </p>
            </div>
          )}

          {migrationStatus.plansNeedingMigration === 0 && migrationStatus.totalPlans > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                <span className="text-green-800 font-medium">Migration Complete</span>
              </div>
              <p className="text-green-700 mt-1">
                All plans have been successfully migrated to support multi-currency pricing.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 p-4 bg-gray-50 rounded-md">
        <h4 className="font-medium text-gray-800 mb-2">What this migration does:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Converts existing single-currency plans to multi-currency format</li>
          <li>• Creates additional Stripe price IDs for EUR and GBP currencies</li>
          <li>• Maintains backward compatibility with existing subscriptions</li>
          <li>• Preserves all existing plan data and settings</li>
        </ul>
      </div>
    </div>
  );
};

export default MigrationManager;
